<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modal.title"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="50%"
  >
    <v-tabs centered v-model="selectedTab">
      <v-tab>{{ $t("RegionalSettings") }}</v-tab>
      <v-tab>{{ $t("Security") }}</v-tab>
      <v-tab>{{ $t("Search") }}</v-tab>
      <v-tab>{{ $t("Email") }}</v-tab>
      <v-tab>{{ $t("DocuSign") }}</v-tab>
      <v-tab>{{ $t("TwoFactorAuthentication") }}</v-tab>
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-text>
            <v-form>
              <v-select
                v-model="setting.defaultLanguageId"
                :items="languageList"
                :label="$t('Language')"
                :error-messages="defaultLanguageIdErrors"
                @change="$v.setting.defaultLanguageId.$touch()"
                @blur="$v.setting.defaultLanguageId.$touch()"
                item-text="text"
                item-value="id"
              ></v-select>

              <v-select
                v-model="setting.defaultTimezone"
                :items="timeZoneList"
                :label="$t('TimeZone')"
                :error-messages="defaultTimezoneErrors"
                @change="$v.setting.defaultTimezone.$touch()"
                @blur="$v.setting.defaultTimezone.$touch()"
                item-text="text"
                item-value="id"
              ></v-select>

              <v-select
                v-model="setting.defaultDateFormat"
                :items="dateFormatList"
                :label="$t('DateFormat')"
                :error-messages="defaultDateFormatErrors"
                @change="$v.setting.defaultDateFormat.$touch()"
                @blur="$v.setting.defaultDateFormat.$touch()"
                item-text="text"
                item-value="id"
              ></v-select>

              <v-select
                v-model="setting.defaultDecimalSeparator"
                :items="decimalSeparatorList"
                :label="$t('DecimalSeparator')"
                :error-messages="defaultDecimalSeparatorErrors"
                @change="$v.setting.defaultDecimalSeparator.$touch()"
                @blur="$v.setting.defaultDecimalSeparator.$touch()"
                item-text="text"
                item-value="id"
              ></v-select>

              <v-select
                v-model="setting.defaultThousandSeparator"
                :items="thousandSeparatorList"
                :label="$t('ThousandSeparator')"
                :error-messages="defaultThousandSeparatorErrors"
                @change="$v.setting.defaultThousandSeparator.$touch()"
                @blur="$v.setting.defaultThousandSeparator.$touch()"
                item-text="text"
                item-value="id"
              ></v-select>
            </v-form>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-text class="my-0 py-0">
            <v-row>
              <v-col cols="4">
                <v-switch
                  v-model="setting.enablePasswordExpiration"
                  :label="$t('PasswordExpiration')"
                  color="green"
                ></v-switch>
              </v-col>
              <v-col cols="8">
                <v-text-field
                  :disabled="setting.enablePasswordExpiration == false"
                  v-model="setting.passwordExpirationDays"
                  :label="$t('Days')"
                  min="1"
                  step="1"
                  type="number"
                  :error-messages="passwordExpirationDaysErrors"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-switch
                  v-model="setting.enableAccountInactivity"
                  :label="$t('MaxDaysOfInactivity')"
                  color="green"
                ></v-switch>
              </v-col>
              <v-col cols="8">
                <v-text-field
                  :disabled="setting.enableAccountInactivity == false"
                  v-model="setting.accountInactivityDays"
                  :label="$t('Days')"
                  min="1"
                  step="1"
                  type="number"
                  :error-messages="accountInactivityDaysErrors"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-switch
                  v-model="setting.enableFailedLoginAttempts"
                  :label="failedLoginAttemptsLabel"
                  color="green"
                ></v-switch>
              </v-col>
              <v-col cols="8">
                <v-text-field
                  :disabled="setting.enableFailedLoginAttempts == false"
                  v-model="setting.failedLoginAttempts"
                  min="1"
                  step="1"
                  type="number"
                  :error-messages="failedLoginAttemptsErrors"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-switch
                  v-model="setting.enableTokenExpiration"
                  disabled
                  :label="$t('ExternalAccessTokenExpiration')"
                  color="green"
                ></v-switch>
              </v-col>
              <v-col cols="8">
                <v-text-field
                  v-model="setting.tokenExpirationMinutes"
                  :disabled="setting.enableTokenExpiration == false"
                  :label="$t('Minutes')"
                  min="1"
                  step="1"
                  type="number"
                  :error-messages="tokenExpirationMinutesErrors"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-switch
                  v-model="setting.enablePasswordStrength"
                  :label="$t('PasswordSecurityRules')"
                  color="green"
                ></v-switch>
              </v-col>
              <v-col cols="8">
                <v-row>
                  <v-col cols="12">
                    <v-row>
                      <v-col cols="12" sm="6" md="6">
                        <v-text-field
                          :disabled="setting.enablePasswordStrength == false"
                          :label="$t('SpecialCharacters')"
                          v-model="setting.passwordStrengthSpecialLength"
                          min="0"
                          step="1"
                          type="number"
                          :error-messages="passwordStrengthSpecialLengthErrors"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="6">
                        <v-text-field
                          :disabled="setting.enablePasswordStrength == false"
                          :label="$t('CapitalLetters')"
                          v-model="setting.passwordStrengthUpperLength"
                          min="0"
                          step="1"
                          type="number"
                          :error-messages="passwordStrengthUpperLengthErrors"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="4" md="4">
                        <v-text-field
                          :disabled="setting.enablePasswordStrength == false"
                          :label="$t('Numbers')"
                          v-model="setting.passwordStrengthNumberLength"
                          min="0"
                          step="1"
                          type="number"
                          :error-messages="passwordStrengthNumberLengthErrors"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="4">
                        <v-text-field
                          :disabled="setting.enablePasswordStrength == false"
                          :label="$t('MaximumLongitude')"
                          v-model="setting.passwordStrengthMaxLength"
                          min="0"
                          step="1"
                          type="number"
                          :error-messages="passwordStrengthMaxLengthErrors"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" md="4">
                        <v-text-field
                          :disabled="setting.enablePasswordStrength == false"
                          :label="$t('MinimumLongitude')"
                          v-model="setting.passwordStrengthMinLength"
                          min="0"
                          step="1"
                          type="number"
                          :error-messages="passwordStrengthMinLengthErrors"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-text>
            <v-text-field
              v-model="setting.maxSearchResults"
              :label="$t('SearchResultsLimit')"
              min="1"
              step="1"
              type="number"
              :error-messages="maxSearchResultsErrors"
              @input="$v.setting.maxSearchResults.$touch()"
              @blur="$v.setting.maxSearchResults.$touch()"
            ></v-text-field>
            <v-text-field
              :label="$t('VisibleDocumentFields')"
              v-model="setting.searchResultsVisibleFields"
              min="1"
              step="1"
              type="number"
              :error-messages="searchResultsVisibleFieldsErrors"
              @input="$v.setting.searchResultsVisibleFields.$touch()"
              @blur="$v.setting.searchResultsVisibleFields.$touch()"
            ></v-text-field>
            <v-row>
              <v-col cols="12" sm="6" md="6">
                <v-switch
                  v-model="setting.searchResultsOrderAsc"
                  color="green"
                  :label="$t('SortResultsAscending')"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="6" md="6" v-show="false">
                <v-switch
                  v-model="setting.enableDocumentPreAuthorization"
                  color="green"
                  :label="$t('EnableDocumentsPreAuthorization')"
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-text>
            <v-select
              v-model="emailMethod"
              :items="emailMethods"
              :label="$t('Type')"
              item-text="text"
              item-value="id"
            ></v-select>

            <v-text-field
              v-show="showSmtpMethod"
              v-model="setting.emailServer"
              :label="$t('Server')"
              :error-messages="emailServerErrors"
              @input="$v.setting.emailServer.$touch()"
              @blur="$v.setting.emailServer.$touch()"
            ></v-text-field>
            <v-text-field
              v-show="showSmtpMethod"
              :label="$t('Port')"
              v-model="setting.emailPort"
              max="50"
              min="1"
              step="1"
              type="number"
              :error-messages="emailPortErrors"
              @input="$v.setting.emailPort.$touch()"
              @blur="$v.setting.emailPort.$touch()"
            ></v-text-field>
            <v-text-field
              v-model="setting.emailAccount"
              :label="$t('Login')"
              :error-messages="emailAccountErrors"
              @input="$v.setting.emailAccount.$touch()"
              @blur="$v.setting.emailAccount.$touch()"
            ></v-text-field>
            <v-text-field
              v-show="showSmtpMethod"
              v-model="setting.emailPassword"
              :label="$t('Password')"
              counter
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showPassword = !showPassword"
              :type="showPassword ? 'text' : 'password'"
              :error-messages="emailPasswordErrors"
              @input="$v.setting.emailPassword.$touch()"
              @blur="$v.setting.emailPassword.$touch()"
            ></v-text-field>
            <v-switch
              v-show="showSmtpMethod"
              color="green"
              v-model="setting.emailUseSSL"
              :label="$t('SSL')"
            ></v-switch>
            <v-text-field
              v-show="!showSmtpMethod"
              v-model="setting.sendGridApiKey"
              :label="$t('API KEY')"
              :error-messages="sendGridApiKeyErrors"
              @input="$v.setting.sendGridApiKey.$touch()"
              @blur="$v.setting.sendGridApiKey.$touch()"
            ></v-text-field>
            <v-row align="center" class="mx-2">
              <v-btn :loading="loading" color="warning" @click="test">{{
                $t("Test")
              }}</v-btn>
              <v-spacer />
              <v-btn color="success" @click="openEmailTemplate"
                >{{ $t("Templates") }}
                <v-icon right dark> mdi-cogs </v-icon>
              </v-btn>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-title>
            <v-icon large left> mdi-draw </v-icon>
            <v-switch
              v-model="setting.docuSignEnabled"
              color="green"
              :label="$t('DocuSign')"
            ></v-switch>
          </v-card-title>
          <v-card-text v-if="setting.docuSignEnabled">
            <v-text-field
              :label="$t('URL')"
              v-model="setting.docuSignApiURL"
            ></v-text-field>
            <v-text-field
              :label="$t('User ID')"
              v-model="setting.docuSignAccountID"
            ></v-text-field>
            <v-text-field
              :label="$t('Integration Key')"
              v-model="setting.docuSignIntegrationKey"
            ></v-text-field>
            <v-textarea
              v-model="setting.docuSignRSAKey"
              :label="$t('RSA Private Key')"
            ></v-textarea>
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-text>
            <v-text-field
              v-model="setting.twoFactorAuthenticationServiceId"
              :label="$t('SERVICE ID')"
              :error-messages="twoFactorAuthenticationServiceIdErrors"
              @input="$v.setting.twoFactorAuthenticationServiceId.$touch()"
              @blur="$v.setting.twoFactorAuthenticationServiceId.$touch()"
            ></v-text-field>
            <v-text-field
              v-model="setting.twoFactorAuthenticationAuthApiKey"
              :label="$t('AUTH API KEY')"
              :error-messages="twoFactorAuthenticationAuthApiKeyErrors"
              @input="$v.setting.twoFactorAuthenticationAuthApiKey.$touch()"
              @blur="$v.setting.twoFactorAuthenticationAuthApiKey.$touch()"
            ></v-text-field>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs>
    <EmailTemplateModal ref="emailTemplateModal" />
  </base-modal>
</template>

<script>
import {
  required,
  requiredIf,
  email,
  minValue,
} from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

import OrganizationService from "@/services/OrganizationService";
import UserService from "@/services/UserService";
import Parameter from "@/ecm/models/Parameter";
import Setting from "@/ecm/models/Setting";
import Utils from "@/ecm/common/Utils";
import EmailTemplateModal from "@/components/administration/organization/EmailTemplateModal.vue";

export default {
  components: {
    EmailTemplateModal,
  },
  created() {
    UserService.getLanguageList().then((data) => {
      this.languageList = data;
    });

    UserService.getTimeZoneList().then((data) => {
      this.timeZoneList = data;
    });

    UserService.getDateFormatList().then((data) => {
      this.dateFormatList = data;
    });

    UserService.getDecimalSeparatorList().then((data) => {
      this.decimalSeparatorList = data;
    });

    UserService.getThousandSeparatorList().then((data) => {
      this.thousandSeparatorList = data;
    });
    UserService.getThousandSeparatorList().then((data) => {
      this.thousandSeparatorList = data;
    });
  },
  data() {
    return {
      modal: {
        title: this.$t("Settings"),
        key: "settingsModal",
      },
      open: false,
      showPassword: false,
      languageList: [],
      timeZoneList: [],
      dateFormatList: [],
      decimalSeparatorList: [],
      thousandSeparatorList: [],
      setting: new Setting(),
      parameter: new Parameter(),
      loading: false,
      emailMethod: 0,
      selectedTab: 0,
      emailMethods: [
        { id: 0, text: "SMTP" },
        { id: 1, text: "SendGrid" },
      ],
    };
  },
  mixins: [validationMixin],
  validations: {
    setting: {
      defaultLanguageId: { required },
      defaultTimezone: { required },
      defaultDateFormat: { required },
      defaultDecimalSeparator: { required },
      defaultThousandSeparator: { required },
      passwordExpirationDays: {
        required: requiredIf(function () {
          return this.setting.enablePasswordExpiration;
        }),
        minValue: minValue(1),
      },
      accountInactivityDays: {
        required: requiredIf(function () {
          return this.setting.enableAccountInactivity;
        }),
        minValue: minValue(1),
      },
      failedLoginAttempts: {
        required: requiredIf(function () {
          return this.setting.enableFailedLoginAttempts;
        }),
        minValue: minValue(1),
      },
      tokenExpirationMinutes: {
        required: requiredIf(function () {
          return this.setting.enableTokenExpiration;
        }),
        minValue: minValue(1),
      },
      passwordStrengthMinLength: {
        required: requiredIf(function () {
          return this.setting.enablePasswordStrength;
        }),
        minValue: minValue(0),
      },
      passwordStrengthMaxLength: {
        required: requiredIf(function () {
          return this.setting.enablePasswordStrength;
        }),
        minValue: minValue(0),
      },
      passwordStrengthSpecialLength: {
        required: requiredIf(function () {
          return this.setting.enablePasswordStrength;
        }),
        minValue: minValue(0),
      },
      passwordStrengthUpperLength: {
        required: requiredIf(function () {
          return this.setting.enablePasswordStrength;
        }),
        minValue: minValue(0),
      },
      passwordStrengthNumberLength: {
        required: requiredIf(function () {
          return this.setting.enablePasswordStrength;
        }),
        minValue: minValue(0),
      },
      searchResultsVisibleFields: { required, minValue: minValue(1) },
      maxSearchResults: { required, minValue: minValue(1) },
      emailAccount: { required, email },
      emailServer: {
        required: requiredIf(function () {
          return this.emailMethod == 0;
        }),
      },
      emailPort: {
        required: requiredIf(function () {
          return this.emailMethod == 0;
        }),
      },
      emailPassword: {
        required: requiredIf(function () {
          return this.emailMethod == 0;
        }),
      },
      sendGridApiKey: {
        required: requiredIf(function () {
          return this.emailMethod == 1;
        }),
      },
      twoFactorAuthenticationServiceId: {
        required: requiredIf(function () {
          return this.setting.twoFactorAuthenticationAuthApiKey;
        }),
      },
      twoFactorAuthenticationAuthApiKey: {
        required: requiredIf(function () {
          return this.setting.twoFactorAuthenticationServiceId;
        }),
      },
    },
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    failedLoginAttemptsLabel() {
      var label = this.$t("FailedLoginAttempts");
      return label.replace(label[0], label[0].toUpperCase());
    },
    showSmtpMethod() {
      return this.emailMethod == 0;
    },
    defaultLanguageIdErrors() {
      const errors = [];
      if (!this.$v.setting.defaultLanguageId.$dirty) return errors;
      !this.$v.setting.defaultLanguageId.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    defaultTimezoneErrors() {
      const errors = [];
      if (!this.$v.setting.defaultTimezone.$dirty) return errors;
      !this.$v.setting.defaultTimezone.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    defaultDateFormatErrors() {
      const errors = [];
      if (!this.$v.setting.defaultDateFormat.$dirty) return errors;
      !this.$v.setting.defaultDateFormat.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    defaultDecimalSeparatorErrors() {
      const errors = [];
      if (!this.$v.setting.defaultDecimalSeparator.$dirty) return errors;
      !this.$v.setting.defaultDecimalSeparator.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    defaultThousandSeparatorErrors() {
      const errors = [];
      if (!this.$v.setting.defaultThousandSeparator.$dirty) return errors;
      !this.$v.setting.defaultThousandSeparator.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    passwordExpirationDaysErrors() {
      const errors = [];
      if (!this.$v.setting.passwordExpirationDays.$dirty) return errors;
      !this.$v.setting.passwordExpirationDays.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.passwordExpirationDays.minValue &&
        errors.push(`${this.$t("MinValue")}: 1`);
      return errors;
    },
    accountInactivityDaysErrors() {
      const errors = [];
      if (!this.$v.setting.accountInactivityDays.$dirty) return errors;
      !this.$v.setting.accountInactivityDays.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.accountInactivityDays.minValue &&
        errors.push(`${this.$t("MinValue")}: 1`);
      return errors;
    },
    failedLoginAttemptsErrors() {
      const errors = [];
      if (!this.$v.setting.failedLoginAttempts.$dirty) return errors;
      !this.$v.setting.failedLoginAttempts.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.failedLoginAttempts.minValue &&
        errors.push(`${this.$t("MinValue")}: 1`);
      return errors;
    },
    tokenExpirationMinutesErrors() {
      const errors = [];
      if (!this.$v.setting.tokenExpirationMinutes.$dirty) return errors;
      !this.$v.setting.tokenExpirationMinutes.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.tokenExpirationMinutes.minValue &&
        errors.push(`${this.$t("MinValue")}: 1`);
      return errors;
    },
    passwordStrengthMinLengthErrors() {
      const errors = [];
      if (!this.$v.setting.passwordStrengthMinLength.$dirty) return errors;
      !this.$v.setting.passwordStrengthMinLength.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.passwordStrengthMinLength.minValue &&
        errors.push(`${this.$t("MinValue")}: 0`);
      return errors;
    },
    passwordStrengthMaxLengthErrors() {
      const errors = [];
      if (!this.$v.setting.passwordStrengthMaxLength.$dirty) return errors;
      !this.$v.setting.passwordStrengthMaxLength.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.passwordStrengthMaxLength.minValue &&
        errors.push(`${this.$t("MinValue")}: 0`);
      return errors;
    },
    passwordStrengthSpecialLengthErrors() {
      const errors = [];
      if (!this.$v.setting.passwordStrengthSpecialLength.$dirty) return errors;
      !this.$v.setting.passwordStrengthSpecialLength.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.passwordStrengthSpecialLength.minValue &&
        errors.push(`${this.$t("MinValue")}: 0`);
      return errors;
    },
    passwordStrengthUpperLengthErrors() {
      const errors = [];
      if (!this.$v.setting.passwordStrengthUpperLength.$dirty) return errors;
      !this.$v.setting.passwordStrengthUpperLength.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.passwordStrengthUpperLength.minValue &&
        errors.push(`${this.$t("MinValue")}: 0`);
      return errors;
    },
    passwordStrengthNumberLengthErrors() {
      const errors = [];
      if (!this.$v.setting.passwordStrengthNumberLength.$dirty) return errors;
      !this.$v.setting.passwordStrengthNumberLength.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.passwordStrengthNumberLength.minValue &&
        errors.push(`${this.$t("MinValue")}: 0`);
      return errors;
    },
    maxSearchResultsErrors() {
      const errors = [];
      if (!this.$v.setting.maxSearchResults.$dirty) return errors;
      !this.$v.setting.maxSearchResults.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.maxSearchResults.minValue &&
        errors.push(`${this.$t("MinValue")}: 1`);
      return errors;
    },
    searchResultsVisibleFieldsErrors() {
      const errors = [];
      if (!this.$v.setting.searchResultsVisibleFields.$dirty) return errors;
      !this.$v.setting.searchResultsVisibleFields.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.setting.searchResultsVisibleFields.minValue &&
        errors.push(`${this.$t("MinValue")}: 1`);
      return errors;
    },
    emailPasswordErrors() {
      const errors = [];
      if (!this.$v.setting.emailPassword.$dirty) return errors;
      !this.$v.setting.emailPassword.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    sendGridApiKeyErrors() {
      const errors = [];
      if (!this.$v.setting.sendGridApiKey.$dirty) return errors;
      !this.$v.setting.sendGridApiKey.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    emailServerErrors() {
      const errors = [];
      if (!this.$v.setting.emailServer.$dirty) return errors;
      !this.$v.setting.emailServer.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    emailAccountErrors() {
      const errors = [];
      if (!this.$v.setting.emailAccount.$dirty) return errors;
      !this.$v.setting.emailAccount.required &&
        errors.push(this.$t("RequiredField"));
      !this.$v.setting.emailAccount.email &&
        errors.push(this.$t("InvalidEmail"));
      return errors;
    },
    emailPortErrors() {
      const errors = [];
      if (!this.$v.setting.emailPort.$dirty) return errors;
      !this.$v.setting.emailPort.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      return errors;
    },
    twoFactorAuthenticationAuthApiKeyErrors() {
      const errors = [];
      if (!this.$v.setting.twoFactorAuthenticationAuthApiKey.$dirty)
        return errors;
      !this.$v.setting.twoFactorAuthenticationAuthApiKey.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    twoFactorAuthenticationServiceIdErrors() {
      const errors = [];
      if (!this.$v.setting.twoFactorAuthenticationServiceId.$dirty)
        return errors;
      !this.$v.setting.twoFactorAuthenticationServiceId.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
  },
  methods: {
    openEmailTemplate() {
      if (!this.isMailSettingsValid()) return;
      this.$refs["emailTemplateModal"].show(this.setting.toDto());
    },
    isMailSettingsValid() {
      if (!this.$v.setting.emailAccount.required) {
        this.$v.setting.emailAccount.$touch();
        return false;
      }

      if (this.emailMethod == 1) {
        if (!this.$v.setting.sendGridApiKey.required) {
          this.$v.setting.sendGridApiKey.$touch();
          return false;
        }
      }

      if (this.emailMethod == 0) {
        if (!this.$v.setting.emailServer.required) {
          this.$v.setting.emailServer.$touch();
          return false;
        }
        if (!this.$v.setting.emailPort.required) {
          this.$v.setting.emailPort.$touch();
          return false;
        }
        if (!this.$v.setting.emailPassword.required) {
          this.$v.setting.emailPassword.$touch();
          return false;
        }
      }

      return true;
    },
    test() {
      if (!this.isMailSettingsValid()) return;
      this.loading = true;
      OrganizationService.testEmailConfig(this.setting.toDto())
        .then((data) => {
          Utils.toast.success(this.$t("EmailSent"));
        })
        .catch((error) => {})
        .then(() => {
          //always
          this.loading = false;
        });
    },
    saveItem() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        if (
          this.$v.setting.defaultLanguageId.$invalid ||
          this.$v.setting.defaultTimezone.$invalid ||
          this.$v.setting.defaultDateFormat.$invalid ||
          this.$v.setting.defaultDecimalSeparator.$invalid ||
          this.$v.setting.defaultThousandSeparator.$invalid
        ) {
          this.selectedTab = 0;
        } else if (
          this.$v.setting.passwordExpirationDays.$invalid ||
          this.$v.setting.accountInactivityDays.$invalid ||
          this.$v.setting.failedLoginAttempts.$invalid ||
          this.$v.setting.tokenExpirationMinutes.$invalid ||
          this.$v.setting.passwordStrengthMinLength.$invalid ||
          this.$v.setting.passwordStrengthMaxLength.$invalid ||
          this.$v.setting.passwordStrengthSpecialLength.$invalid ||
          this.$v.setting.passwordStrengthUpperLength.$invalid ||
          this.$v.setting.passwordStrengthNumberLength.$invalid
        ) {
          this.selectedTab = 1;
        } else if (
          this.$v.setting.searchResultsVisibleFields.$invalid ||
          this.$v.setting.maxSearchResults.$invalid
        ) {
          this.selectedTab = 2;
        } else if (
          this.$v.setting.emailAccount.$invalid ||
          this.$v.setting.emailServer.$invalid ||
          this.$v.setting.emailPort.$invalid ||
          this.$v.setting.emailPassword.$invalid ||
          this.$v.setting.sendGridApiKey.$invalid
        ) {
          this.selectedTab = 3;
        } else if (
          this.$v.setting.twoFactorAuthenticationServiceId.$invalid ||
          this.$v.setting.twoFactorAuthenticationAuthApiKey.$invalid
        ) {
          this.selectedTab = 4;
        }
        return;
      }

      if (this.emailMethod == 0) this.setting.sendGridApiKey = "";
      else {
        this.setting.emailServer = "";
        this.setting.emailPassword = "";
        this.setting.emailPort = 0;
      }

      this.modalInstance.progress(true);

      OrganizationService.saveSettings(this.setting.toDto())
        .then((result) => {
          if (result) {
            Utils.toast.success(this.$t("Settings") + " " +this.$t("UpdatedSuccessfully"));
            this.hide();
          }
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    new() {
      OrganizationService.getSettings().then((data) => {
        this.setting = new Setting(data);
        this.emailMethod = this.setting.sendGridApiKey ? 1 : 0;
      });
      this.show();
    },
    show() {
      this.open = true;
      this.selectedTab = 0;
    },
    hide() {
      this.open = false;
      this.selectedTab = 0;
    },
  },
};
</script>
